import { CircleCheck } from "lucide-react";
import { TitlePill } from "../../web/components/RecordWindow";
import { can, usePriceListSaver } from "./data";
import { PillSelect } from "./ListToolbar";
import { CHECKED_BY, type CheckedKind, type CheckedMark, type PriceListData } from "./meta";

/*
 * Checked (checked.ts) wherever an item, product, bundle, supplier or group shows: in its window's title bar, to mark it or take
 * the mark off, and a green tick before its name in the lists (each with an Only: Not checked / Checked). A change to it takes the
 * mark off by itself.
 */

/** "Checked by Matt on 4 Nov 2026" (or just when, if who isn't known any more). */
export const checkedText = (mark: CheckedMark) =>
    `Checked${mark.by ? ` by ${mark.by}` : ""} on ${new Date(mark.at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;

/** In its window's title bar: checked or not, clicked to change (by whoever may change it). Not on an archived one. */
export function CheckedPill({ data, kind, id, onError }: Readonly<{ data: PriceListData; kind: CheckedKind; id: string; onError: (err: Error) => void }>) {
    const saver = usePriceListSaver();
    const mark = data.checked[id];
    const editable = can(data, CHECKED_BY[kind]);
    if (!mark && !editable) return null;
    const toggle = editable ? () => saver.setChecked(kind, id, !mark).catch(onError) : undefined;
    return (
        <TitlePill
            on={mark !== undefined}
            title={mark ? `${checkedText(mark)}. Any change takes it off.${editable ? " Click to take it off now." : ""}` : "Mark it as gone over and right. Any change takes it off."}
            onClick={toggle}
        >
            <CircleCheck className="size-3.5" />
            {mark ? "Checked" : "Mark checked"}
        </TitlePill>
    );
}

/** Before its name in a list: a green tick, if it's checked. */
export function CheckedListMark({ mark }: Readonly<{ mark: CheckedMark | undefined }>) {
    return mark ? (
        <span className="inline-flex shrink-0" title={checkedText(mark)}>
            <CircleCheck className="size-3.5 text-brand-ink" aria-label="Checked" />
        </span>
    ) : null;
}

/** A list narrowed to those checked, or not yet ("": all of them). */
export type CheckedFilter = "" | "checked" | "notChecked";

export const inCheckedFilter = (filter: CheckedFilter, data: PriceListData, id: string) => !filter || (data.checked[id] !== undefined) === (filter === "checked");

/** Beside a list's search (bundles, suppliers, groups): Only: Not checked / Checked. */
export function CheckedOnly({ value, onChange }: Readonly<{ value: CheckedFilter; onChange: (v: CheckedFilter) => void }>) {
    return (
        <PillSelect
            label="Only"
            value={value}
            anyLabel="Everything"
            options={[
                { value: "notChecked", label: "Not checked" },
                { value: "checked", label: "Checked" },
            ]}
            onChange={(v) => onChange(v as CheckedFilter)}
        />
    );
}
